/**
 * Licence margin report logic (#1142): bands, chip predicates, renewal windows and sums. Pure functions only, so the
 * three tabs and their tests share one definition of every number.
 *
 * Absence is not zero (hard rule 10): every reader returns `null` for a missing value and every sum says how many
 * lines it could not price; nothing here turns a missing figure into 0.
 */
import type { LicenceMarginReport, LicenceMarginRow, LineNote, UsageMarginRow } from '../../../lib/api/generated'
import { formatWhen } from '../../../lib/display-time'

export type Notes = NonNullable<LicenceMarginReport['notes']>

export const num = (v: string | number | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

// --- bands ----------------------------------------------------------------------------------------------------------

export type MarginBand = 'negative' | 'u5' | 'u10' | 'u20' | 'ok'

export const MARGIN_BANDS: { band: MarginBand; label: string }[] = [
  { band: 'negative', label: 'Negative' },
  { band: 'u5', label: 'Under 5%' },
  { band: 'u10', label: 'Under 10%' },
  { band: 'u20', label: 'Under 20%' },
  { band: 'ok', label: '20% or more' },
]
export const MARGIN_LABEL = Object.fromEntries(MARGIN_BANDS.map((b) => [b.band, b.label])) as Record<MarginBand, string>
const BAND_RANK: Record<MarginBand, number> = { negative: 0, u5: 1, u10: 2, u20: 3, ok: 4 }

export function marginBand(m: number | null): MarginBand | null {
  if (m === null) return null
  return m < 0 ? 'negative' : m < 5 ? 'u5' : m < 10 ? 'u10' : m < 20 ? 'u20' : 'ok'
}

/** The worse of two bands (a missing one never wins). */
export function worstBand(bands: (MarginBand | null)[]): MarginBand | null {
  let out: MarginBand | null = null
  for (const b of bands) if (b && (out === null || BAND_RANK[b] < BAND_RANK[out])) out = b
  return out
}

/** Signed difference from MSRP: at or above retail, then up to 5%, 5-15% and over 15% below it. */
export type MsrpBand = 'ok' | 'u10' | 'u5' | 'negative'

export const MSRP_BANDS: { band: MsrpBand; label: string }[] = [
  { band: 'ok', label: 'At or above MSRP' },
  { band: 'u10', label: 'Up to 5% below' },
  { band: 'u5', label: '5–15% below' },
  { band: 'negative', label: 'Over 15% below' },
]
export const MSRP_LABEL = Object.fromEntries(MSRP_BANDS.map((b) => [b.band, b.label])) as Record<MsrpBand, string>

/** `pctOfMsrp` is the billed rate as % of MSRP (100 = at retail). */
export function msrpBand(pctOfMsrp: number | null): { band: MsrpBand; diff: number } | null {
  if (pctOfMsrp === null) return null
  const diff = pctOfMsrp - 100
  return { band: diff >= 0 ? 'ok' : diff > -5 ? 'u10' : diff > -15 ? 'u5' : 'negative', diff }
}

// --- formats --------------------------------------------------------------------------------------------------------

const CAD = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', currencyDisplay: 'narrowSymbol' })
/** Money with cents: per-seat prices need them at any size. */
export const money = (n: number): string => CAD.format(n)
export const pct = (n: number): string => `${n <= -0.05 ? '−' : ''}${Math.abs(n).toFixed(1)}%`
export const signed = (n: number, fmt: (x: number) => string): string => `${n > 0 ? '+' : n < 0 ? '−' : '±'}${fmt(Math.abs(n))}`
export const qtyText = (n: number | null | undefined): string => (n === null || n === undefined ? '' : String(+n))

// --- licences tab ---------------------------------------------------------------------------------------------------

/** Statuses whose margin is not a customer billing margin (no chip, band or total counts them). */
const EXCLUDED: ReadonlySet<string> = new Set([
  'Internal',
  'Zero-cost',
  'Usage-based',
  'Customer not found',
  'Pooled under Ewee',
  'Billed, not in Pax8',
])
/** Statuses that need no pill beside the licence (the margin cells already say it). */
export const QUIET_STATUS: ReadonlySet<string> = new Set(['OK', 'Below cost now', 'Below cost at renewal', 'Thin at renewal', 'Thin now'])
export const ALARM_STATUS: ReadonlySet<string> = new Set(['Not billed', 'Billed, not in Pax8'])

/** A line we bill a customer for, so its margin counts. */
export const isBilledLine = (r: LicenceMarginRow): boolean => num(r.billed_rate) !== null && !EXCLUDED.has(r.status ?? '')

export const marginNow = (r: LicenceMarginRow): number | null => (isBilledLine(r) ? num(r.margin_pct_now) : null)
export const marginAtRenewal = (r: LicenceMarginRow): number | null => (isBilledLine(r) ? num(r.margin_pct_at_renewal) : null)
const lowest = (r: LicenceMarginRow): number | null => {
  const a = marginNow(r)
  const b = marginAtRenewal(r)
  return a === null ? b : b === null ? a : Math.min(a, b)
}
const between = (v: number | null, lo: number, hi: number) => v !== null && v >= lo && v < hi
const lt = (v: number | null, x: number) => v !== null && v < x

export const underMsrp = (r: LicenceMarginRow): boolean => {
  const bill = num(r.billed_rate)
  const msrp = num(r.msrp)
  return isBilledLine(r) && bill !== null && msrp !== null && bill < msrp
}

export interface ChipDef<T> {
  id: string
  label: string
  test: (row: T, notes: Notes) => boolean
}

const hasNote = (key: string | null | undefined, notes: Notes) => !!key && !!notes[key]

export const LICENCE_CHIPS: ChipDef<LicenceMarginRow>[] = [
  {
    id: 'action',
    label: 'Needs action',
    test: (r) =>
      (isBilledLine(r) && (lt(marginNow(r), 10) || lt(marginAtRenewal(r), 10))) || ALARM_STATUS.has(r.status ?? '') || !!r.books_fix,
  },
  { id: 'neg-now', label: 'Negative now', test: (r) => lt(marginNow(r), 0) },
  { id: 'neg-ren', label: 'Negative at renewal', test: (r) => lt(marginAtRenewal(r), 0) },
  { id: 'u5', label: 'Under 5%', test: (r) => between(lowest(r), 0, 5) },
  { id: 'u10', label: 'Under 10%', test: (r) => between(lowest(r), 5, 10) },
  { id: 'u20', label: 'Under 20%', test: (r) => between(lowest(r), 10, 20) },
  { id: 'msrp', label: 'Under MSRP', test: underMsrp },
  { id: 'fix', label: 'Books to fix', test: (r) => !!r.books_fix },
  { id: 'nb', label: 'Not billed / not in Pax8', test: (r) => ALARM_STATUS.has(r.status ?? '') },
  { id: 'noted', label: 'Has a note', test: (r, n) => hasNote(r.key, n) },
  { id: 'all', label: 'All', test: () => true },
]

/** A sum over the lines that have the value, and how many lines lacked it. `value` is null when none had it. */
export interface Total {
  value: number | null
  missing: number
}

export function total<T>(rows: T[], pick: (r: T) => number | null): Total {
  let value: number | null = null
  let missing = 0
  for (const r of rows) {
    const v = pick(r)
    if (v === null) missing++
    else value = (value ?? 0) + v
  }
  return { value, missing }
}

/** Margin % from revenue and cost totals; null when either is unknown or revenue is 0. */
export const marginOf = (rev: number | null, cost: number | null): number | null =>
  rev !== null && cost !== null && rev !== 0 ? ((rev - cost) / rev) * 100 : null

export function licenceSummary(rows: LicenceMarginRow[]) {
  const billed = rows.filter(isBilledLine)
  const rev = total(billed, (r) => num(r.monthly_revenue))
  const costNow = total(billed, (r) => num(r.monthly_cost_now))
  const costRen = total(billed, (r) => num(r.monthly_cost_at_renewal))
  return {
    revenue: rev,
    costNow,
    costRenewal: costRen,
    marginNow: marginOf(rev.value, costNow.value),
    marginRenewal: marginOf(rev.value, costRen.value),
    negativeNow: billed.filter((r) => lt(marginNow(r), 0)).length,
    negativeRenewal: billed.filter((r) => lt(marginAtRenewal(r), 0)).length,
    underMsrp: rows.filter(underMsrp).length,
    booksFix: rows.filter((r) => !!r.books_fix).length,
  }
}

export const isAnnualPrice = (r: { term?: string | null }) => !!r.term && /^annual/i.test(r.term.trim())
export const perSeat = (r: { term?: string | null }) => (isAnnualPrice(r) ? 'per seat per year' : 'per seat per month')

/** Detail lines for a margin cell: the per-seat prices, and what the margin is worth a month on the seats. */
export function marginLines(r: LicenceMarginRow, cost: number | null, when: string): string[] {
  const bill = num(r.billed_rate)
  if (bill === null || cost === null) return []
  const seats = r.billed_qty ?? r.pax8_qty ?? null
  const perMonth = isAnnualPrice(r) ? 12 : 1
  const lines = [`${when}: we bill ${money(bill)}, cost ${money(cost)} ${perSeat(r)}`]
  lines.push(
    seats === null
      ? `Margin ${signed(bill - cost, money)} per seat; seat count unknown`
      : `Margin ${signed(bill - cost, money)} per seat, ${signed(((bill - cost) * seats) / perMonth, money)} a month on ${seats} seat${seats === 1 ? '' : 's'}`,
  )
  return lines
}

export function matchesSearch(fields: (string | null | undefined)[], q: string): boolean {
  const needle = q.trim().toLowerCase()
  return !needle || fields.join(' ').toLowerCase().includes(needle)
}

// --- usage tab ------------------------------------------------------------------------------------------------------

/** Only compare with MSRP when Pax8's retail price covers the whole cost of the line. */
export function usageUnderMsrp(r: UsageMarginRow): boolean {
  const billed = num(r.billed)
  const msrp = num(r.msrp)
  const covers = num(r.msrp_covers_pct)
  return billed !== null && msrp !== null && (covers === null || covers >= 99) && billed < msrp
}

export const USAGE_CHIPS: ChipDef<UsageMarginRow>[] = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'neg', label: 'Negative', test: (r) => lt(num(r.margin_pct), 0) },
  { id: 'low', label: 'Under 10%', test: (r) => between(num(r.margin_pct), 0, 10) },
  { id: 'u20', label: 'Under 20%', test: (r) => between(num(r.margin_pct), 10, 20) },
  { id: 'msrp', label: 'Under MSRP', test: usageUnderMsrp },
  { id: 'nb', label: 'Not billed', test: (r) => num(r.billed) === null },
  { id: 'noted', label: 'Has a note', test: (r, n) => hasNote(r.key, n) },
]

export function usageSummary(rows: UsageMarginRow[]) {
  const billedRows = rows.filter((r) => num(r.billed) !== null)
  const unbilled = rows.filter((r) => num(r.billed) === null)
  const cost = total(rows, (r) => num(r.cost))
  const billed = total(billedRows, (r) => num(r.billed))
  const billedCost = total(billedRows, (r) => num(r.cost))
  return {
    cost,
    billed,
    margin: billedCost.missing ? null : marginOf(billed.value, billedCost.value),
    negative: rows.filter((r) => lt(num(r.margin_pct), 0)).length,
    notBilled: unbilled.length,
    unbilledCost: total(unbilled, (r) => num(r.cost)),
  }
}

// --- renewals tab ---------------------------------------------------------------------------------------------------

const DAY_MS = 86_400_000
export const utcDay = (iso: string): number => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`)
export const monthKey = (iso: string): string => iso.slice(0, 7)

// Calendar keys (YYYY-MM, YYYY-MM-DD) are labelled from their digits, never through a time zone (#1231).
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const monthName = (key: string): string => MONTHS[Number(key.slice(5, 7)) - 1] ?? key
export const monthLabel = (key: string, long = false): string =>
  long ? `${monthName(key)} ${key.slice(0, 4)}` : `${monthName(key).slice(0, 3)} ${key.slice(2, 4)}`
export const dayLabel = (iso: string): string => `${monthName(iso).slice(0, 3)} ${Number(iso.slice(8, 10))}, ${iso.slice(0, 4)}`

/** A term commitment (renews once a year). "Monthly / 1-Year" is a monthly-billed annual commitment. */
export function isCommit(r: { term?: string | null }): boolean {
  const term = r.term ?? ''
  const commitment = term.includes('/') ? term.split('/').slice(1).join('/') : term
  return /(1-Year|Annual|1 Year|Yearly|3-Year|3 Year)/i.test(commitment)
}

export type RenewalWindow = '30' | '90' | '365'
export const RENEWAL_WINDOWS: { id: RenewalWindow; label: string; days: number }[] = [
  { id: '30', label: 'Next 30 days', days: 30 },
  { id: '90', label: 'Next 90 days', days: 90 },
  { id: '365', label: 'Next 12 months', days: 366 },
]

/** Cost a month once the renewal price applies (the current price when Pax8 gives no renewal price). */
export const renewalCost = (r: LicenceMarginRow): number | null => num(r.monthly_cost_at_renewal) ?? num(r.monthly_cost_now)
/** Monthly cost change at renewal; unknown when either side is unknown. */
export function renewalDelta(r: LicenceMarginRow): number | null {
  const now = num(r.monthly_cost_now)
  const ren = num(r.monthly_cost_at_renewal)
  return now === null || ren === null ? null : ren - now
}
/** What we bill a month on a customer line (internal and unbilled lines bill nothing we can count). */
export const billedMonthly = (r: LicenceMarginRow): number | null => (isBilledLine(r) ? num(r.monthly_revenue) : null)
/** The 12-month commitment at the renewal price, for term commitments. */
export const commitment = (r: LicenceMarginRow): number | null => {
  const c = renewalCost(r)
  return isCommit(r) && c !== null ? c * 12 : null
}

export interface RenewalScope {
  days: number
  m2m: boolean
  internal: boolean
}

export const daysUntil = (iso: string, asOf: number): number => Math.round((utcDay(iso) - asOf) / DAY_MS)

/** Lines renewing within `days` of the as-of day that carry money, in scope. */
export function renewalRows(rows: LicenceMarginRow[], asOf: number, scope: RenewalScope): LicenceMarginRow[] {
  return rows.filter((r) => {
    if (!r.renews) return false
    const d = daysUntil(r.renews, asOf)
    if (d < 0 || d >= scope.days) return false
    if (r.status === 'Zero-cost') return false
    if (![num(r.monthly_revenue), num(r.monthly_cost_now), num(r.monthly_cost_at_renewal)].some((v) => v !== null && v !== 0)) return false
    if (!isCommit(r) && !scope.m2m) return false
    if (r.status === 'Internal' && !scope.internal) return false
    return true
  })
}

export interface RenewalSummary {
  n: number
  billed: Total
  cost: Total
  /** Sum of the cost increases and decreases at renewal (lines with both prices). */
  change: Total
  negative: number
}

export function renewalSummary(rows: LicenceMarginRow[]): RenewalSummary {
  return {
    n: rows.length,
    billed: total(rows.filter(isBilledLine), billedMonthly),
    cost: total(rows, renewalCost),
    change: total(rows, renewalDelta),
    negative: rows.filter((r) => lt(marginAtRenewal(r), 0)).length,
  }
}

export const RENEWAL_CHIPS: ChipDef<LicenceMarginRow>[] = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'neg', label: 'Negative at renewal', test: (r) => lt(marginAtRenewal(r), 0) },
  { id: 'thin', label: 'Under 10% at renewal', test: (r) => between(marginAtRenewal(r), 0, 10) },
  { id: 'up', label: 'Cost goes up', test: (r) => (renewalDelta(r) ?? 0) > 0.05 },
  { id: 'noted', label: 'Has a note', test: (r, n) => hasNote(r.key, n) },
]

/** Dot colour on the timeline: the margin band at renewal, internal lines their own, unknown margin neutral. */
export type DotTone = MarginBand | 'internal' | 'unknown'
const DOT_RANK: Record<DotTone, number> = { negative: 0, u5: 1, u10: 2, u20: 3, ok: 4, unknown: 5, internal: 6 }
export const dotTone = (r: LicenceMarginRow): DotTone =>
  r.status === 'Internal' ? 'internal' : (marginBand(marginAtRenewal(r)) ?? 'unknown')
export const worstDot = (tones: DotTone[]): DotTone => [...tones].sort((a, b) => DOT_RANK[a] - DOT_RANK[b])[0] ?? 'unknown'

/** The timeline's span: the first of the as-of month for 13 months, as month buckets in percent of the width. */
export function timelineSpan(asOf: number) {
  const d0 = new Date(asOf)
  const start = Date.UTC(d0.getUTCFullYear(), d0.getUTCMonth(), 1)
  const end = Date.UTC(d0.getUTCFullYear(), d0.getUTCMonth() + 13, 1)
  const x = (t: number) => Math.min(100, Math.max(0, ((t - start) / (end - start)) * 100))
  const months: { key: string; a: number; b: number }[] = []
  for (let i = 0; i < 13; i++) {
    const a = Date.UTC(d0.getUTCFullYear(), d0.getUTCMonth() + i, 1)
    const b = Date.UTC(d0.getUTCFullYear(), d0.getUTCMonth() + i + 1, 1)
    months.push({ key: new Date(a).toISOString().slice(0, 7), a: x(a), b: x(b) })
  }
  return { start, end, x, months }
}

/** Hover lines for one licence on a renewal: Pax8 price now -> at renewal, margin now -> at renewal. */
export function renewalLines(r: LicenceMarginRow): string[] {
  const per = isAnnualPrice(r) ? 'a seat a year' : 'a seat a month'
  const lines = [`${qtyText(r.pax8_qty) || '?'} × ${r.product}${r.status === 'Internal' ? ' (internal)' : ''}`]
  const now = num(r.cost_now)
  const ren = num(r.cost_at_renewal)
  const change = renewalDelta(r)
  if (now !== null && ren !== null) {
    const dc = ren - now
    lines.push(
      Math.abs(dc) < 0.005
        ? `Pax8 price ${money(now)} ${per}, no change`
        : `Pax8 price ${money(now)} → ${money(ren)} ${per} (${signed(dc, money)}${now ? `, ${signed((dc / now) * 100, pct)}` : ''})`,
    )
    if (change !== null && Math.abs(change) > 0.05) lines.push(`Cost ${signed(change, money)} a month`)
  } else {
    lines.push('Pax8 price at renewal unknown')
  }
  const mNow = marginNow(r)
  const mRen = marginAtRenewal(r)
  const billed = billedMonthly(r)
  if (billed !== null) lines.push(`We bill ${money(billed)} a month`)
  if (mNow !== null && mRen !== null) {
    const dm = mRen - mNow
    lines.push(
      Math.abs(dm) < 0.05
        ? `Margin ${pct(mNow)}, no change`
        : `Margin ${pct(mNow)} → ${pct(mRen)} (${signed(dm, (x) => `${x.toFixed(1)} pts`)}${change === null ? '' : `, ${signed(-change, money)} a month`})`,
    )
  }
  return lines
}

export function noteLabel(note: LineNote): string {
  const when = Number.isNaN(new Date(note.at).getTime()) ? '' : formatWhen(note.at, { style: 'date' })
  return [note.by?.name ?? 'Someone', when].filter(Boolean).join(' · ')
}

// --- sorting --------------------------------------------------------------------------------------------------------

export interface SortState {
  key: string
  dir: 1 | -1
}

/** Sort by a column's value: numbers numerically, text alphabetically; a missing value always goes last. */
export function sortBy<T>(rows: T[], sort: SortState, value: (row: T, key: string) => string | number | null | undefined): T[] {
  return [...rows].sort((a, b) => {
    const av = value(a, sort.key)
    const bv = value(b, sort.key)
    const aMissing = av === null || av === undefined || av === ''
    const bMissing = bv === null || bv === undefined || bv === ''
    if (aMissing || bMissing) return aMissing === bMissing ? 0 : aMissing ? 1 : -1
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sort.dir
    return String(av).localeCompare(String(bv)) * sort.dir
  })
}
